import { BusinessVerificationStatus } from "@sp-crm/core";
import type { BadgeProps } from "components/ui/badge";
import type { BusinessProfileOutput } from "./use-business-verification";

export type CustomerTrustProduct = BusinessProfileOutput["trustProducts"][number];
export type CustomerTrustProductStatus = CustomerTrustProduct["status"];
export type TrustProductType = CustomerTrustProduct["type"];

export type StatusTone = "ok" | "info" | "bad" | "idle";

export interface CustomerStatusStyle {
    label: string;
    tone: StatusTone;
}

export const customerTrustProductStyles: Record<
    CustomerTrustProductStatus,
    CustomerStatusStyle
> = {
    notStarted: { label: "Starts after verification", tone: "idle" },
    inReview: { label: "In review", tone: "info" },
    active: { label: "Active", tone: "ok" },
    needsAttention: { label: "Needs attention", tone: "bad" },
};

export interface BusinessStatusStyle extends CustomerStatusStyle {
    badgeType: BadgeProps["type"];
}

export const customerBusinessStyles: Record<
    BusinessVerificationStatus,
    BusinessStatusStyle
> = {
    draft: { label: "In progress", tone: "info", badgeType: "info" },
    inReview: { label: "In review", tone: "info", badgeType: "info" },
    needsAttention: { label: "Needs attention", tone: "bad", badgeType: "error" },
    verified: { label: "Verified", tone: "ok", badgeType: "success" },
};

export const customerTextingStyles: Record<
    BusinessVerificationStatus,
    CustomerStatusStyle
> = {
    draft: customerTrustProductStyles.notStarted,
    inReview: customerTrustProductStyles.notStarted,
    needsAttention: customerTrustProductStyles.notStarted,
    verified: customerTrustProductStyles.inReview,
};

interface CustomerTrustProductDescription {
    name: string;
    what: string;
    rejection: string;
}

export const customerTrustProductDescriptions: Record<
    TrustProductType,
    CustomerTrustProductDescription
> = {
    shakenStir: {
        name: "Signed calls",
        what: "Calls are signed as coming from your business",
        rejection: "Carriers need a detail checked. Our team will contact you.",
    },
    voiceIntegrity: {
        name: "Spam protection",
        what: "Your numbers are registered with carriers' spam labeling services",
        rejection: "Carriers need a detail checked. Our team will contact you.",
    },
    cnam: {
        name: "Caller ID name",
        what: "A short version of your business name shows when you call",
        rejection:
            "Carriers didn't accept your caller ID name. Our team will contact you.",
    },
};

const worstFirst: CustomerTrustProductStatus[] = [
    "needsAttention",
    "inReview",
    "notStarted",
    "active",
];

export const callingStatusOf = (
    products: CustomerTrustProduct[],
): CustomerTrustProductStatus => {
    const statuses = new Set(products.map(p => p.status));
    return worstFirst.find(status => statuses.has(status)) ?? "notStarted";
};

export const businessNeedsAttention = (profile: BusinessProfileOutput): boolean =>
    profile.verificationStatus === "needsAttention" ||
    callingStatusOf(profile.trustProducts) === "needsAttention";
